import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { toast } from 'sonner';

export interface SubscriptionCost {
  id: string;
  shop_id: string;
  subscription_id: string;
  cost: number;
  created_at: string;
  updated_at: string;
}

/**
 * Hook for subscription costs - MANAGER ONLY
 * Returns empty array if user is not a manager (RLS will block anyway)
 */
export function useSubscriptionCosts(assetId: string | null) {
  const { activeShop } = useAuth();
  const { isManager } = usePermissions();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['subscription-costs', assetId],
    queryFn: async () => {
      if (!assetId || !shopId || !isManager) return [];
      
      // Get subscription IDs for this asset first
      const { data: subscriptions } = await supabase
        .from('shop_asset_subscriptions')
        .select('id')
        .eq('asset_id', assetId)
        .eq('shop_id', shopId);
      
      if (!subscriptions || subscriptions.length === 0) return [];
      
      const subscriptionIds = subscriptions.map(s => s.id);
      
      const { data, error } = await supabase
        .from('shop_asset_subscription_costs')
        .select('*')
        .eq('shop_id', shopId)
        .in('subscription_id', subscriptionIds);
      
      if (error) throw error;
      return data as SubscriptionCost[];
    },
    enabled: !!assetId && !!shopId && isManager,
  });
}

export function useSetSubscriptionCost() {
  const { activeShop } = useAuth();
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async (input: { subscription_id: string; cost: number; asset_id: string }) => {
      if (!shopId) throw new Error('No active shop');
      if (!canEditAssets) throw new Error('You do not have permission to edit subscription costs');

      // Upsert - check if exists first
      const { data: existing } = await supabase
        .from('shop_asset_subscription_costs')
        .select('id')
        .eq('subscription_id', input.subscription_id)
        .eq('shop_id', shopId)
        .maybeSingle();
      
      if (existing) {
        const { data, error } = await supabase
          .from('shop_asset_subscription_costs')
          .update({ cost: input.cost })
          .eq('id', existing.id)
          .select()
          .single();
        
        if (error) throw error;
        return { ...data, asset_id: input.asset_id };
      } else {
        const { data, error } = await supabase
          .from('shop_asset_subscription_costs')
          .insert({
            shop_id: shopId,
            subscription_id: input.subscription_id,
            cost: input.cost,
          })
          .select()
          .single();
        
        if (error) throw error;
        return { ...data, asset_id: input.asset_id };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subscription-costs', data.asset_id] });
      toast.success('Subscription cost saved');
    },
    onError: (error) => {
      toast.error('Failed to save subscription cost', { description: error.message });
    },
  });
}

/**
 * Calculate total subscription costs for an asset
 */
export function useTotalSubscriptionCost(assetId: string | null) {
  const { data: costs, isLoading } = useSubscriptionCosts(assetId);
  
  const total = costs?.reduce((sum, c) => sum + Number(c.cost), 0) ?? 0;
  
  return { total, isLoading };
}
